import { Asset, Folder, FolderContent } from '@/api/generatedApi';
import { ContentDragProvider } from '@/components/AssetsPage/ContentDragContext';
import { useFolderDnd } from '@/components/AssetsPage/useFolderDnd';
import { ContentsTable } from '@/components/ContentsTable/ContentsTable';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { BulkMoveContentsModal } from '@/components/modals/BulkMoveContentsModal/BulkMoveContentsModal';
import { TargetFolder, toTargetFolder } from '@/components/modals/BulkMoveContentsModal/targetFolderUtils';
import { ROOT_FOLDER_ID } from '@/utils/folders';
import { FileInputs } from '@/components/Upload/FileInputs/FileInputs';
import { UploadDrawer } from '@/components/Upload/UploadDrawer/UploadDrawer';
import { useMoveAsset } from '@/hooks/api/assets/useMoveAsset';
import { useFetchFolderContents } from '@/hooks/api/folders/useFetchFolderContents';
import { useMoveFolder } from '@/hooks/api/folders/useMoveFolder';
import { useGetFolder } from '@/hooks/api/folders/useGetFolder';
import { useGetRootFolder } from '@/hooks/api/folders/useGetRootFolder';
import { useFolderColumnVisibility } from '@/hooks/api/folders/useFolderColumnVisibility';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { useViewMode } from '@/hooks/useViewMode';
import { getAssetFileName } from '@/utils/assets';
import { useAssetUploadContext } from '@/services/assetUpload/useAssetUploadContext';
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { memo, useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const MemoizedContentsTable = memo(ContentsTable);

const ContentArea = styled(Box)({
  flex: 1,
  minWidth: '400px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const AssetsPageInner = ({ folderId }: { folderId: string | undefined }) => {
  const [viewMode, setViewMode] = useViewMode('global');
  const { isEmpty: isRootFolderEmpty, isLoading: isRootFolderLoading } = useGetRootFolder({ enabled: !folderId });
  const { fetchData, error } = useFetchFolderContents({ folderId });
  const { folder, isLoading: isFolderLoading } = useGetFolder({ folderId });

  const pageTitle = folder ? folder.name : 'Assets';
  usePageTitle(pageTitle);

  const isFolderEmpty =
    !isFolderLoading &&
    !isRootFolderLoading &&
    (folderId ? (folder?.assetsCount ?? 0) + (folder?.foldersCount ?? 0) === 0 : isRootFolderEmpty);

  const { permissions } = useGetCurrentUser();

  const {
    hasDescriptions,
    hasLabels,
    labelOptions,
    metadataFieldNames,
    isLoading: isLoadingFieldNames,
  } = useFolderColumnVisibility({ folderId });

  const { metadataColumns, columnVisibilityModel, onColumnVisibilityModelChange, isMetadataLoading } =
    useColumnVisibility({
      metadataFieldNames,
      isLoadingFieldNames,
      canEditMetadata: permissions.canModifyAssetMetadata,
      contextKey: folderId || 'root',
      filterable: true,
      hasDescriptions,
      hasLabels,
    });

  const {
    files,
    uploadProgress,
    drawerOpen,
    handleDrawerClose,
    fileInputProps,
    fileInputReference,
    folderInputReference,
  } = useAssetUploadContext();

  const { moveFolder } = useMoveFolder();
  const { moveAsset } = useMoveAsset();
  const { showToast } = useToast();

  const [bulkMoveState, setBulkMoveState] = useState<
    | {
        contents: FolderContent[];
        targetFolder?: TargetFolder;
        selectionMode?: boolean;
      }
    | undefined
  >();
  const [optimisticRemovedIds, setOptimisticRemovedIds] = useState<string[]>([]);
  const [folderContentsVersion, setFolderContentsVersion] = useState(0);

  useEffect(() => {
    setOptimisticRemovedIds([]);
  }, [folderId]);

  const handleMoveContents = (contents: FolderContent[], targetFolderId: string, targetFolderName?: string) => {
    const isRoot = targetFolderId === 'root';
    const newParentIdForApi = isRoot ? undefined : targetFolderId;

    if (contents.length > 1) {
      setBulkMoveState({
        contents,
        targetFolder: toTargetFolder({ id: isRoot ? ROOT_FOLDER_ID : targetFolderId, name: targetFolderName }),
      });
      return;
    }

    const content = contents[0];
    setOptimisticRemovedIds([content.id]);

    const clearOptimistic = () => setOptimisticRemovedIds([]);

    if (content.contentType === 'folder') {
      const draggedFolder = content as Folder;
      const originalParentFolderId = draggedFolder.parentFolderId;

      moveFolder(
        { folder: draggedFolder, parentFolderId: newParentIdForApi },
        {
          onSuccess: () => {
            setFolderContentsVersion(v => v + 1);
            showToast({
              message: `Moved "${draggedFolder.name}"`,
              type: 'success',
              action: {
                label: 'Undo',
                onClick: () => {
                  moveFolder({
                    folder: { ...draggedFolder, parentFolderId: newParentIdForApi },
                    parentFolderId: originalParentFolderId,
                  });
                },
              },
            });
          },
          onError: error => {
            clearOptimistic();
            const message = error.response?.data?.message ?? 'Failed to move folder';
            showToast({ message, type: 'error' });
          },
        }
      );
    } else {
      const asset = content as Asset;
      const originalFolderId = asset.folderId ?? undefined;

      moveAsset(
        { asset, folderId: newParentIdForApi },
        {
          onSuccess: () => {
            setFolderContentsVersion(v => v + 1);
            showToast({
              message: `Moved "${getAssetFileName(asset)}"`,
              type: 'success',
              action: {
                label: 'Undo',
                onClick: () => {
                  moveAsset({
                    asset: { ...asset, folderId: newParentIdForApi },
                    folderId: originalFolderId,
                  });
                },
              },
            });
          },
          onError: error => {
            clearOptimistic();
            const message = error.response?.data?.message ?? 'Failed to move asset';
            showToast({ message, type: 'error' });
          },
        }
      );
    }
  };

  const handleCloseBulkMoveModal = () => {
    setBulkMoveState(undefined);
  };

  const handleTargetSelected = (target?: TargetFolder) => {
    setBulkMoveState(previous => (previous ? { ...previous, targetFolder: target } : undefined));
  };

  const handleDropOnHiddenFolders = (contents: FolderContent[]) => {
    setBulkMoveState({ contents, targetFolder: undefined, selectionMode: true });
  };

  const { sensors, handleDragStart, handleDragOver, handleDragEnd, handleDragCancel, dragOverlay } = useFolderDnd({
    onMoveContents: handleMoveContents,
    onDropOnHiddenFolders: handleDropOnHiddenFolders,
  });

  const toolbarActions = useMemo(
    () => <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />,
    [viewMode, setViewMode]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[snapCenterToCursor]}
    >
      <Box sx={{ flex: 1, minHeight: 0, padding: '6px 6px 6px 0', display: 'flex', flexDirection: 'column' }}>
        <MemoizedContentsTable
          fetchContents={fetchData}
          currentFolder={folder}
          error={error}
          isFolderEmpty={isFolderEmpty}
          isLoading={isFolderLoading || isRootFolderLoading}
          metadataColumns={metadataColumns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          isColumnDataLoading={isMetadataLoading}
          enableFolderDragging={permissions.canCreateFolders}
          viewMode={viewMode}
          toolbarActions={toolbarActions}
          optimisticRemovedContentIds={optimisticRemovedIds}
          folderContentsVersion={folderContentsVersion}
          labelOptions={labelOptions}
        />
      </Box>
      {/* dropAnimation={null} disables snap-back; dnd-kit API requires null to disable (undefined = default) */}
      {/* eslint-disable-next-line unicorn/no-null -- dnd-kit dropAnimation requires null to disable */}
      <DragOverlay dropAnimation={null}>{dragOverlay}</DragOverlay>
      <FileInputs
        fileInputProps={fileInputProps}
        fileInputReference={fileInputReference}
        folderInputReference={folderInputReference}
      />
      <UploadDrawer files={files} uploadProgress={uploadProgress} isOpen={drawerOpen} onClose={handleDrawerClose} />
      <BulkMoveContentsModal
        isOpen={bulkMoveState !== undefined}
        onClose={handleCloseBulkMoveModal}
        contents={bulkMoveState?.contents ?? []}
        targetFolder={bulkMoveState?.targetFolder}
        selectionMode={bulkMoveState?.selectionMode}
        onTargetSelected={handleTargetSelected}
      />
    </DndContext>
  );
};

export const AssetsPageContent = () => {
  const [searchParameters] = useSearchParams();
  const folderId: string | undefined = searchParameters.get('folderId') ?? undefined;

  return (
    <ContentDragProvider currentFolderId={folderId}>
      <AssetsPageInner folderId={folderId} />
    </ContentDragProvider>
  );
};

export const AssetsPage = () => {
  return (
    <ContentArea>
      <AssetsPageContent />
    </ContentArea>
  );
};
